import { CutDirection } from "./cutUtils";

const KERF = 4.4;
const EPS = 1e-6;

// ─────────────────────────────────────────────
// Beam Search alapértelmezett konfiguráció
// ─────────────────────────────────────────────
export const DEFAULT_BEAM_CONFIG = {
  // Hány részleges megoldást tartunk párhuzamosan
  beamWidth: 16,

  // Hány heurisztikus kezdő sorrendet generálunk
  seedCount: 24,

  // Hulladék vs táblahasználat súlyozása (0=csak hulladék, 1=csak tábla)
  wasteWeight: 0.5,
  tableWeight: 0.5,

  // Ha igaz, a legjobb beam-iteráció eredményét használjuk
  // (lassabb de pontosabb)
  fullBeam: true
};

let globalCutIdCounter = 1;

function pushCut(cuts, cut) {
  const cutWithId = {
    ...cut,
    cutId: globalCutIdCounter++,
    abs: cut.abs ?? null,
    spanStart: cut.spanStart ?? null,
    spanEnd: cut.spanEnd ?? null
  };
  cuts.push(cutWithId);
  return cutWithId;
}

// Replay must retain empty bands left by deleted sectors. The separating
// kerf belongs to the gap, so the waste cut stops one kerf before the next part.
function cutLayoutGap(cuts, sector, start, next, TRIM) {
  const gap = next - start;
  if (gap < KERF - EPS) return 0;
  const horizontal = sector.dir === CutDirection.HORIZONTAL;
  if (sector.level === 0) {
    pushCut(cuts, {
      vertical: !horizontal, pos: TRIM, abs: TRIM,
      isTrim: true, isSecondTrim: true, isWaste: true, level: sector.level,
      rootMainDir: sector.rootMainDir,
      spanStart: start, spanEnd: next - KERF
    });
  }
  pushCut(cuts, {
    vertical: horizontal, pos: Math.max(0, gap - KERF),
    abs: next - KERF, level: sector.level, isWaste: true,
    isInnerBandCut: sector.level > 0,
    isInnerBandCutX: sector.level > 1,
    rootMainDir: sector.rootMainDir,
    spanStart: horizontal ? sector.y : sector.x,
    spanEnd: horizontal ? sector.y + sector.h : sector.x + sector.w
  });
  // The empty main sector needs its own RX row after Y, just like a filled one.
  if (sector.level === 0) {
    pushCut(cuts, {
      vertical: !horizontal, pos: TRIM, abs: TRIM,
      isTrim: true, isSecondTrim: true, isWaste: true, level: sector.level,
      rootMainDir: sector.rootMainDir,
      spanStart: start, spanEnd: next - KERF
    });
  }
  return gap;
}

export function createSector({
  tableIndex,
  x,
  y,
  w,
  h,
  dir,
  level,
  rootMainDir,
  isFullHeight = false,
  preferredStartRect = null
}) {
  return {
    tableIndex,
    x,
    y,
    w,
    h,
    dir,
    level,
    rootMainDir,
    isFullHeight,
    preferredStartRect
  };
}

function debugSector(id, label, sector, extra = {}) {
  /*if (Number(id) !== -5) return;
  console.log(`[TABLE -5] ${label}`, {
    dir: sector.dir,
    level: sector.level,
    rootMainDir: sector.rootMainDir,
    x: sector.x,
    y: sector.y,
    w: sector.w,
    h: sector.h,
    right: sector.x + sector.w,
    bottom: sector.y + sector.h,
    ...extra
  });*/
}

function isBetterByOldRule(
  candidateRemaining,
  candidateWaste,
  bestRemaining,
  bestWaste
) {
  if (candidateRemaining < bestRemaining - EPS) return true;
  if (Math.abs(candidateRemaining - bestRemaining) < EPS) {
    return candidateWaste < bestWaste - EPS;
  }
  return false;
}

function sizeDiff(aW, aH, bW, bH) {
  return Math.abs(aW - bW) + Math.abs(aH - bH);
}

function hasValidKerfRemainder(sectorSpan, partSpan) {
  const remainder = sectorSpan - partSpan;
  if (remainder < -EPS) return false;
  return remainder <= EPS || remainder >= KERF - EPS;
}

function getCrossSpan(sector, width, height) {
  return sector.dir === CutDirection.VERTICAL ? width : height;
}

function getMainSpan(sector, width, height) {
  return sector.dir === CutDirection.VERTICAL ? height : width;
}

function isBetterAfterPrev(current, best) {
  if (!best) return true;
  if (current.crossSpan > best.crossSpan + EPS) return true;
  if (best.crossSpan > current.crossSpan + EPS) return false;
  if (current.mainSpan > best.mainSpan + EPS) return true;
  if (best.mainSpan > current.mainSpan + EPS) return false;
  if (current.diff < best.diff - EPS) return true;
  if (best.diff < current.diff - EPS) return false;
  if (current.area > best.area + EPS) return true;
  if (best.area > current.area + EPS) return false;
  return isBetterByOldRule(
    current.remaining,
    current.waste,
    best.remaining,
    best.waste
  );
}

function pickBestThatFits(
  sector,
  remainingRects,
  placedRects,
  placeX,
  placeY,
  prevRect = null,
  isSectorStart = false,
  preferredStartRect = null
) {
  let bestExact = null,
    bestExactIndex = -1;
  let bestExactRemaining = Infinity,
    bestExactWaste = Infinity;

  let bestLargest = null,
    bestLargestIndex = -1;
  let bestLargestArea = -Infinity;
  let bestLargestRemaining = Infinity,
    bestLargestWaste = Infinity;

  let bestStartMatch = null,
    bestStartMatchIndex = -1;

  let bestStartMatchCrossDiff = Infinity,
    bestStartMatchTotalDiff = Infinity,
    bestStartMatchArea = -Infinity;

  let bestStartMatchRemaining = Infinity,
    bestStartMatchWaste = Infinity;

  let bestAfterPrev = null,
    bestAfterPrevIndex = -1;

  let bestFallback = null,
    bestFallbackIndex = -1;
  let bestFallbackRemaining = Infinity,
    bestFallbackWaste = Infinity;

  const sectorRight = sector.x + sector.w;
  const sectorBottom = sector.y + sector.h;

  function preferredMetrics(width, height, preferred) {
    if (!preferred) {
      return {
        crossDiff: Infinity,
        totalDiff: Infinity
      };
    }

    const candidates = [
      {
        width: preferred.width,
        height: preferred.height
      }
    ];

    // placedRect-ben a width/height tartalmazhat KERF-et,
    // ezért ezt is figyelembe vesszük
    if (preferred.width > KERF && preferred.height > KERF) {
      candidates.push({
        width: preferred.width - KERF,
        height: preferred.height - KERF
      });
    }

    let best = {
      crossDiff: Infinity,
      totalDiff: Infinity
    };

    for (const p of candidates) {
      const crossDiff = Math.abs(
        getCrossSpan(sector, width, height) -
          getCrossSpan(sector, p.width, p.height)
      );

      const totalDiff = sizeDiff(width, height, p.width, p.height);

      if (
        crossDiff < best.crossDiff - EPS ||
        (Math.abs(crossDiff - best.crossDiff) < EPS &&
          totalDiff < best.totalDiff - EPS)
      ) {
        best = {
          crossDiff,
          totalDiff
        };
      }
    }

    return best;
  }

  for (let i = 0; i < remainingRects.length; i++) {
    const r = remainingRects[i];
    const tryOrientations = [
      { width: r.width, height: r.height, rotated: false }
    ];
    if (r.rotable) {
      tryOrientations.push({ width: r.height, height: r.width, rotated: true });
    }

    for (const o of tryOrientations) {
      const pw = o.width;
      const ph = o.height;
      if (pw <= EPS || ph <= EPS) continue;

      const xEnd = placeX + pw;
      const yEnd = placeY + ph;
      if (xEnd > sectorRight + EPS || yEnd > sectorBottom + EPS) continue;
      const hasValidWidthRemainder = hasValidKerfRemainder(sector.w, pw);
      const hasValidHeightRemainder = hasValidKerfRemainder(sector.h, ph);

      if (!hasValidWidthRemainder || !hasValidHeightRemainder) {
        continue;
      }
      const remaining = sector.w - pw;
      const waste = (sector.w - pw) * sector.h + (sector.h - ph) * pw;
      const area = pw * ph;

      const candidate = {
        ...r,
        width: o.width,
        height: o.height,
        rotated: o.rotated
      };

      if (
        !bestFallback ||
        isBetterByOldRule(
          remaining,
          waste,
          bestFallbackRemaining,
          bestFallbackWaste
        )
      ) {
        bestFallback = candidate;
        bestFallbackIndex = i;
        bestFallbackRemaining = remaining;
        bestFallbackWaste = waste;
      }

      if (isSectorStart) {
        if (preferredStartRect) {
          const metrics = preferredMetrics(
            o.width,
            o.height,
            preferredStartRect
          );

          if (
            !bestStartMatch ||
            metrics.crossDiff < bestStartMatchCrossDiff - EPS ||
            (Math.abs(metrics.crossDiff - bestStartMatchCrossDiff) < EPS &&
              metrics.totalDiff < bestStartMatchTotalDiff - EPS) ||
            (Math.abs(metrics.crossDiff - bestStartMatchCrossDiff) < EPS &&
              Math.abs(metrics.totalDiff - bestStartMatchTotalDiff) < EPS &&
              area > bestStartMatchArea + EPS) ||
            (Math.abs(metrics.crossDiff - bestStartMatchCrossDiff) < EPS &&
              Math.abs(metrics.totalDiff - bestStartMatchTotalDiff) < EPS &&
              Math.abs(area - bestStartMatchArea) < EPS &&
              isBetterByOldRule(
                remaining,
                waste,
                bestStartMatchRemaining,
                bestStartMatchWaste
              ))
          ) {
            bestStartMatch = candidate;
            bestStartMatchIndex = i;
            bestStartMatchCrossDiff = metrics.crossDiff;
            bestStartMatchTotalDiff = metrics.totalDiff;
            bestStartMatchArea = area;
            bestStartMatchRemaining = remaining;
            bestStartMatchWaste = waste;
          }
        }

        const longSide = Math.max(pw, ph);
        const shortSide = Math.min(pw, ph);

        if (
          !bestLargest ||
          longSide > bestLargest.longSide + EPS ||
          (Math.abs(longSide - bestLargest.longSide) < EPS &&
            shortSide > bestLargest.shortSide + EPS) ||
          (Math.abs(longSide - bestLargest.longSide) < EPS &&
            Math.abs(shortSide - bestLargest.shortSide) < EPS &&
            area > bestLargestArea + EPS) ||
          (Math.abs(longSide - bestLargest.longSide) < EPS &&
            Math.abs(shortSide - bestLargest.shortSide) < EPS &&
            Math.abs(area - bestLargestArea) < EPS &&
            isBetterByOldRule(
              remaining,
              waste,
              bestLargestRemaining,
              bestLargestWaste
            ))
        ) {
          bestLargest = {
            ...candidate,
            longSide,
            shortSide
          };

          bestLargestIndex = i;
          bestLargestArea = area;
          bestLargestRemaining = remaining;
          bestLargestWaste = waste;
        }

        continue;
      }

      if (prevRect) {
        const sameAsPrev =
          Math.abs(o.width - prevRect.width) < EPS &&
          Math.abs(o.height - prevRect.height) < EPS;

        if (sameAsPrev) {
          if (
            !bestExact ||
            isBetterByOldRule(
              remaining,
              waste,
              bestExactRemaining,
              bestExactWaste
            )
          ) {
            bestExact = candidate;
            bestExactIndex = i;
            bestExactRemaining = remaining;
            bestExactWaste = waste;
          }
          continue;
        }

        const diff = sizeDiff(
          o.width,
          o.height,
          prevRect.width,
          prevRect.height
        );
        const currentAfterPrev = {
          rect: candidate,
          index: i,
          crossSpan: getCrossSpan(sector, o.width, o.height),
          mainSpan: getMainSpan(sector, o.width, o.height),
          diff,
          area,
          remaining,
          waste
        };

        if (isBetterAfterPrev(currentAfterPrev, bestAfterPrev)) {
          bestAfterPrev = currentAfterPrev;
          bestAfterPrevIndex = i;
        }
      }
    }
  }

  if (!isSectorStart && bestExact)
    return { rect: bestExact, index: bestExactIndex };
  if (isSectorStart && bestStartMatch)
    return { rect: bestStartMatch, index: bestStartMatchIndex };
  if (isSectorStart && bestLargest)
    return { rect: bestLargest, index: bestLargestIndex };
  if (!isSectorStart && prevRect && bestAfterPrev)
    return { rect: bestAfterPrev.rect, index: bestAfterPrevIndex };
  return bestFallback ? { rect: bestFallback, index: bestFallbackIndex } : null;
}

function pickByExistingLayout(
  sector,
  remainingRects,
  placedRects,
  tableIndex,
  id,
  placeX,
  placeY,
  availW,
  availH,
  debug = true
) {
  const sectorRight = placeX + availW;
  const sectorBottom = placeY + availH;

  if (debug) {
    console.groupCollapsed("🔎 REPLAY PICK");
    console.log("SECTOR", { x: placeX, y: placeY, w: availW, h: availH });
  }

  let best = null,
    bestIndex = -1;

  for (let i = 0; i < remainingRects.length; i++) {
    const r = remainingRects[i];
    if (r.id !== id) continue;

    const effectiveWidth = r.rotated ? r.height : r.width;
    const effectiveHeight = r.rotated ? r.width : r.height;
    const pw = effectiveWidth;
    const ph = effectiveHeight;

    const xEnd = r.xpos + pw;
    const yEnd = r.ypos + ph;

    if (debug) {
      console.log(
        "CANDIDATE",
        { xpos: r.xpos, ypos: r.ypos, width: r.width, height: r.height },
        r
      );
    }

    const tol = KERF + 1e-3;
    if (
      r.xpos < placeX - tol ||
      r.ypos < placeY - tol ||
      xEnd > sectorRight + tol ||
      yEnd > sectorBottom + tol
    )
      continue;

    const candidate = {
      ...r,
      width: effectiveWidth,
      height: effectiveHeight,
      rotated: r.rotated
    };

    if (!best) {
      best = candidate;
      bestIndex = i;
    } else {
      if (sector.dir === CutDirection.HORIZONTAL) {
        if (
          r.ypos < best.ypos - EPS ||
          (Math.abs(r.ypos - best.ypos) < EPS && r.xpos < best.xpos)
        ) {
          best = candidate;
          bestIndex = i;
        }
      } else {
        if (
          r.xpos < best.xpos - EPS ||
          (Math.abs(r.xpos - best.xpos) < EPS && r.ypos < best.ypos)
        ) {
          best = candidate;
          bestIndex = i;
        }
      }
    }
  }

  if (debug) {
    if (best)
      console.log("🎯 PICKED", {
        xpos: best.xpos,
        ypos: best.ypos,
        width: best.width,
        height: best.height
      });
    else console.warn("🚨 NO MATCH FOUND");
    console.groupEnd();
  }

  return best ? { rect: best, index: bestIndex } : null;
}

// ─────────────────────────────────────────────
// Eredeti processSectorFillOnTheFly (változatlan)
// ─────────────────────────────────────────────
export function processSectorFillOnTheFly(
  sector,
  remainingRects,
  placedRects,
  tableIndex,
  id,
  cuts,
  mode = "optimize",
  TRIM
) {
  if (!sector || remainingRects.length === 0) return;
  const useExistingLayout = mode === "replay" || mode === "layout";
  const debugReplay = mode === "replay";
  const isRoot = sector.level === 0;

  debugSector(id, "ENTER SECTOR", sector, {
    tableIndex,
    remainingCount: remainingRects.length,
    placedCount: placedRects.length
  });

  if (sector.dir === CutDirection.HORIZONTAL) {
    let x = sector.x;
    let availW = sector.w;
    const startIndex = placedRects.length;
    let stoppedBecauseNoFit = false;
    let prevRectInThisSector = null;

    while (availW > EPS && remainingRects.length) {
      const pick =
        useExistingLayout
          ? pickByExistingLayout(
              sector,
              remainingRects,
              placedRects,
              tableIndex,
              id,
              x,
              sector.y,
              availW,
              sector.h,
              debugReplay
            )
          : pickBestThatFits(
              { ...sector, x, w: availW },
              remainingRects,
              placedRects,
              x,
              sector.y,
              prevRectInThisSector,
              prevRectInThisSector == null,
              sector.preferredStartRect ?? null
            );

      if (!pick) {
        if (debugReplay) console.error("Replay layout mismatch");
        stoppedBecauseNoFit = true;
        break;
      }

      const { rect, index } = pick;
      const pw = rect.width;
      const ph = rect.height;
      if (pw <= EPS || ph <= EPS) {
        remainingRects.splice(index, 1);
        continue;
      }

      if (isRoot && cuts.length === 0) {
        pushCut(cuts, {
          vertical: true,
          pos: TRIM,
          isTrim: true,
          level: sector.level,
          isSecondTrim: false,
          rootMainDir: sector.rootMainDir,
          elementId: rect.mainid ?? rect.localid,
          abs: TRIM,
          spanStart: sector.y,
          spanEnd: sector.y + sector.h
        });
      }

      if (useExistingLayout) {
        const gap = cutLayoutGap(cuts, sector, x, rect.xpos, TRIM);
        x += gap;
        availW -= gap;
      }

      // Decide before subtracting the kerf: even zero leftover waste needs a cut.
      const rightDiff = availW - pw;
      if (rightDiff >= KERF - EPS) {
        pushCut(cuts, {
          vertical: true,
          pos: pw,
          level: sector.level,
          isInnerBandCut: sector.level > 0,
          abs: x + pw,
          spanStart: sector.y,
          spanEnd: sector.y + sector.h,
          // Level 1 still cuts the main strip; deeper strips need V.
          isInnerBandCutX: sector.level > 1,
          rootMainDir: sector.rootMainDir,
          elementId: rect.mainid ?? rect.localid
        });
      }

      if (isRoot) {
        pushCut(cuts, {
          vertical: false,
          pos: TRIM,
          isTrim: true,
          level: sector.level,
          abs: TRIM,
          isSecondTrim: true,
          rootMainDir: sector.rootMainDir,
          elementId: rect.mainid ?? rect.localid,
          spanStart: sector.x,
          spanEnd: sector.x + sector.w
        });
      }

      const belowDiff = sector.h - ph;
      const belowH = sector.h - (ph + KERF);
      let releaseCutId = null;

      if (belowDiff >= KERF - EPS) {
        const isV = sector.rootMainDir === "HORIZONTAL" && sector.level > 0;
        const crossCut = pushCut(cuts, {
          vertical: false,
          level: sector.level,
          abs: sector.y + ph,
          pos: ph,
          spanStart: x,
          spanEnd: x + pw,
          isInnerBandCut: sector.level > 0,
          isInnerBandCutX: isV,
          rootMainDir: sector.rootMainDir,
          elementId: rect.mainid ?? rect.localid
        });
        releaseCutId = crossCut.cutId;
      } else {
        releaseCutId = cuts.length > 0 ? cuts[cuts.length - 1].cutId : null;
      }

      const placedRect =
        useExistingLayout
          ? { ...rect, releaseCutId, isLastInSector: false }
          : {
              ...rect,
              xpos: x,
              ypos: sector.y,
              width: pw + KERF,
              height: ph + KERF,
              tableIndex,
              id,
              rotated: rect.rotated === true,
              rootMainDir: sector.rootMainDir,
              bandKey: sector.rootBandCoord,
              releaseCutId,
              isLastInSector: false
            };

      placedRects.push(placedRect);
      remainingRects.splice(index, 1);

      prevRectInThisSector = {
        ...rect,
        width: rect.width,
        height: rect.height,
        rotated: rect.rotated === true
      };

      if (belowH > EPS) {
        const childX = useExistingLayout ? rect.xpos : x;
        const childY =
          useExistingLayout ? rect.ypos + ph + KERF : sector.y + ph + KERF;
        processSectorFillOnTheFly(
          createSector({
            tableIndex,
            x: childX,
            y: childY,
            w: pw,
            h: belowH,
            dir: CutDirection.VERTICAL,
            level: sector.level + 1,
            rootMainDir: sector.rootMainDir,
            isFullHeight: false,
            preferredStartRect: placedRect
          }),
          remainingRects,
          placedRects,
          tableIndex,
          id,
          cuts,
          mode,
          TRIM
        );
      }

      x += pw + KERF;
      availW -= pw + KERF;
    }

    const placedCount = placedRects.length - startIndex;
    if (
      (placedCount > 0 &&
        sector.level <= 1 &&
        (stoppedBecauseNoFit || availW <= EPS)) ||
      remainingRects.length === 0
    ) {
      placedRects[placedRects.length - 1].isLastInSector = true;
    }
    return;
  } else {
    // VERTICAL SECTOR
    let y = sector.y;
    let availH = sector.h;
    const startIndex = placedRects.length;
    let stoppedBecauseNoFit = false;
    let prevRectInThisSector = null;

    while (availH > EPS && remainingRects.length) {
      const pick =
        useExistingLayout
          ? pickByExistingLayout(
              sector,
              remainingRects,
              placedRects,
              tableIndex,
              id,
              sector.x,
              y,
              sector.w,
              availH,
              debugReplay
            )
          : pickBestThatFits(
              { ...sector, y, h: availH },
              remainingRects,
              placedRects,
              sector.x,
              y,
              prevRectInThisSector,
              prevRectInThisSector == null,
              sector.preferredStartRect ?? null
            );

      if (!pick) {
        if (debugReplay) console.error("Replay layout mismatch");
        stoppedBecauseNoFit = true;
        break;
      }

      const { rect, index } = pick;
      const pw = rect.width;
      const ph = rect.height;
      if (pw <= EPS || ph <= EPS) {
        remainingRects.splice(index, 1);
        continue;
      }

      if (isRoot && cuts.length === 0) {
        pushCut(cuts, {
          vertical: false,
          pos: TRIM,
          abs: TRIM,
          isTrim: true,
          level: sector.level,
          isSecondTrim: false,
          rootMainDir: sector.rootMainDir,
          elementId: rect.mainid ?? rect.localid,
          spanStart: sector.x,
          spanEnd: sector.x + sector.w
        });
      }

      if (useExistingLayout) {
        const gap = cutLayoutGap(cuts, sector, y, rect.ypos, TRIM);
        y += gap;
        availH -= gap;
      }

      const belowDiff = availH - ph;
      let mainCutId = null;

      if (belowDiff >= KERF - EPS) {
        const mainCut = pushCut(cuts, {
          vertical: false,
          level: sector.level,
          isInnerBandCut: sector.level > 0,
          isInnerBandCutX: sector.level > 1,
          pos: ph,
          abs: y + ph,
          spanStart: sector.x,
          spanEnd: sector.x + sector.w,
          rootMainDir: sector.rootMainDir,
          elementId: rect.mainid ?? rect.localid
        });
        mainCutId = mainCut.cutId;
      }

      if (isRoot) {
        pushCut(cuts, {
          vertical: true,
          pos: TRIM,
          abs: TRIM,
          isTrim: true,
          level: sector.level,
          isSecondTrim: true,
          rootMainDir: sector.rootMainDir,
          elementId: rect.mainid ?? rect.localid,
          spanStart: sector.y,
          spanEnd: sector.y + sector.h
        });
      }

      const rightDiff = sector.w - pw;
      const rightW = sector.w - (pw + KERF);
      let releaseCutId;

      if (rightDiff >= KERF - EPS) {
        const crossCut = pushCut(cuts, {
          vertical: true,
          pos: pw,
          abs: sector.x + pw,
          spanStart: y,
          spanEnd: y + ph,
          level: sector.level,
          isInnerBandCut: sector.level > 0,
          isInnerBandCutX: sector.level > 0,
          rootMainDir: sector.rootMainDir,
          elementId: rect.mainid ?? rect.localid
        });
        releaseCutId = crossCut.cutId;
      } else {
        releaseCutId = cuts.length > 0 ? cuts[cuts.length - 1].cutId : null;
      }

      const placedRect =
        useExistingLayout
          ? { ...rect, releaseCutId, isLastInSector: false }
          : {
              ...rect,
              xpos: sector.x,
              ypos: y,
              width: pw + KERF,
              height: ph + KERF,
              tableIndex,
              id,
              rotated: rect.rotated === true,
              rootMainDir: sector.rootMainDir,
              bandKey: sector.rootBandCoord,
              releaseCutId,
              isLastInSector: false
            };

      placedRects.push(placedRect);
      remainingRects.splice(index, 1);

      prevRectInThisSector = {
        ...rect,
        width: rect.width,
        height: rect.height,
        rotated: rect.rotated === true
      };

      if (rightW > EPS) {
        const childX =
          useExistingLayout ? rect.xpos + pw + KERF : sector.x + pw + KERF;
        const childY = useExistingLayout ? rect.ypos : y;
        processSectorFillOnTheFly(
          createSector({
            tableIndex,
            x: childX,
            y: childY,
            w: rightW,
            h: ph,
            dir: CutDirection.HORIZONTAL,
            level: sector.level + 1,
            rootMainDir: sector.rootMainDir,
            isFullHeight: sector.isFullHeight,
            preferredStartRect: placedRect
          }),
          remainingRects,
          placedRects,
          tableIndex,
          id,
          cuts,
          mode,
          TRIM
        );
      }

      y += ph + KERF;
      availH -= ph + KERF;
    }

    const placedCount = placedRects.length - startIndex;
    if (
      (placedCount > 0 &&
        sector.level <= 1 &&
        (stoppedBecauseNoFit || availH <= EPS)) ||
      remainingRects.length === 0
    ) {
      placedRects[placedRects.length - 1].isLastInSector = true;
    }
    return;
  }
}

// ─────────────────────────────────────────────
// Kihasználtság számoló (hulladék + tábla score)
// ─────────────────────────────────────────────
function scoreResult(placedRects, totalRects, tableW, tableH, config) {
  const tableArea = tableW * tableH;
  const usedArea = placedRects.reduce((sum, r) => {
    const pw = r.width - KERF;
    const ph = r.height - KERF;
    return sum + (pw > 0 && ph > 0 ? pw * ph : 0);
  }, 0);

  // Hulladék arány [0..1], kisebb = jobb
  const wasteRatio = tableArea > 0 ? Math.max(0, 1 - usedArea / tableArea) : 1;

  // Elhelyezett arány [0..1], nagyobb = jobb
  const placedRatio = totalRects > 0 ? placedRects.length / totalRects : 0;

  // Kombinált score: kisebb = jobb
  const score =
    config.wasteWeight * wasteRatio - config.tableWeight * placedRatio;

  return { score, wasteRatio, placedRatio, usedArea };
}

// ─────────────────────────────────────────────
// Heurisztikus seed sorrendek generálása
// ─────────────────────────────────────────────
function generateSeedOrders(rects, count) {
  const seeds = [];

  const byArea = [...rects].sort(
    (a, b) => b.width * b.height - a.width * a.height
  );
  seeds.push(byArea);

  const byAreaAsc = [...rects].sort(
    (a, b) => a.width * a.height - b.width * b.height
  );
  seeds.push(byAreaAsc);

  const byWidth = [...rects].sort((a, b) => b.width - a.width);
  seeds.push(byWidth);

  const byHeight = [...rects].sort((a, b) => b.height - a.height);
  seeds.push(byHeight);

  // Szélesség/Magasság arány szerinti rendezések
  const byRatioDesc = [...rects].sort(
    (a, b) => b.width / b.height - a.width / a.height
  );
  seeds.push(byRatioDesc);

  const byRatioAsc = [...rects].sort(
    (a, b) => a.width / a.height - b.width / b.height
  );
  seeds.push(byRatioAsc);

  // Kerület szerint
  const byPerimeter = [...rects].sort(
    (a, b) => b.width + b.height - (a.width + a.height)
  );
  seeds.push(byPerimeter);

  // Max dim szerint
  const byMaxDim = [...rects].sort(
    (a, b) => Math.max(b.width, b.height) - Math.max(a.width, a.height)
  );
  seeds.push(byMaxDim);

  // Vegyes (area desc + ratio shuffle)
  const mixed1 = [...rects].sort((a, b) => {
    const aScore = a.width * a.height * (a.width / a.height);
    const bScore = b.width * b.height * (b.width / b.height);
    return bScore - aScore;
  });
  seeds.push(mixed1);

  const mixed2 = [...rects].sort((a, b) => {
    const aScore = a.width * a.height * (a.height / a.width);
    const bScore = b.width * b.height * (b.height / b.width);
    return bScore - aScore;
  });
  seeds.push(mixed2);

  // Véletlen permutációk a maradék helyekre
  while (seeds.length < count) {
    const shuffled = [...rects];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    seeds.push(shuffled);
  }

  return seeds.slice(0, count);
}

// ─────────────────────────────────────────────
// Egy szektor futtatása adott rect sorrenddel
// (nem módosítja az eredeti tömböket)
// ─────────────────────────────────────────────
function runWithOrder(
  sector,
  rectOrder,
  tableIndex,
  id,
  tableW,
  tableH,
  config
) {
  const remainingClone = rectOrder.map((r) => ({ ...r }));
  const placedClone = [];
  const cutsClone = [];

  // Átmeneti globalCutIdCounter elmentése
  const savedCounter = globalCutIdCounter;

  processSectorFillOnTheFly(
    sector,
    remainingClone,
    placedClone,
    tableIndex,
    id,
    cutsClone,
    "optimize"
  );

  // Counter visszaállítása (a legjobb futás majd újra lefut)
  globalCutIdCounter = savedCounter;

  const { score, wasteRatio, placedRatio } = scoreResult(
    placedClone,
    rectOrder.length,
    tableW,
    tableH,
    config
  );

  return {
    score,
    wasteRatio,
    placedRatio,
    order: rectOrder,
    placed: placedClone.length
  };
}

// ─────────────────────────────────────────────
// Beam search lépés: szomszéd generálás
// ─────────────────────────────────────────────
function generateNeighbors(order, count) {
  const neighbors = [];
  const n = order.length;

  // Swap szomszédok
  for (let k = 0; k < count && k < (n * (n - 1)) / 2; k++) {
    const i = Math.floor(Math.random() * n);
    let j = Math.floor(Math.random() * (n - 1));
    if (j >= i) j++;
    const neighbor = [...order];
    [neighbor[i], neighbor[j]] = [neighbor[j], neighbor[i]];
    neighbors.push(neighbor);
  }

  // Szegmens-reverz szomszédok
  for (let k = 0; k < Math.ceil(count / 3); k++) {
    const i = Math.floor(Math.random() * n);
    const len = 2 + Math.floor(Math.random() * Math.min(6, n - 2));
    const j = Math.min(i + len, n);
    const neighbor = [...order];
    neighbor.splice(i, j - i, ...order.slice(i, j).reverse());
    neighbors.push(neighbor);
  }

  return neighbors;
}

// ─────────────────────────────────────────────
// Fő beam search optimalizáló
// ─────────────────────────────────────────────
export function optimizeSectorFill(
  sector,
  inputRects,
  tableIndex,
  id,
  tableW,
  tableH,
  config = DEFAULT_BEAM_CONFIG,
  TRIM
) {
  const cfg = { ...DEFAULT_BEAM_CONFIG, ...config };

  if (inputRects.length === 0)
    return { placedRects: [], cuts: [], remainingRects: [] };

  // 1) Seed sorrendek generálása
  const seeds = generateSeedOrders(inputRects, cfg.seedCount);

  // 2) Minden seed értékelése
  let beamCandidates = seeds.map((order) =>
    runWithOrder(sector, order, tableIndex, id, tableW, tableH, cfg)
  );

  // Rendezés: kisebb score = jobb
  beamCandidates.sort((a, b) => a.score - b.score);

  // Top-K megtartása
  let beam = beamCandidates.slice(0, cfg.beamWidth);

  // 3) Beam search iterációk
  if (cfg.fullBeam) {
    const iterations = Math.ceil(Math.log2(inputRects.length + 1)) + 2;

    for (let iter = 0; iter < iterations; iter++) {
      const nextCandidates = [...beam];

      for (const candidate of beam) {
        const neighbors = generateNeighbors(
          candidate.order,
          Math.ceil(cfg.beamWidth / beam.length) + 2
        );

        for (const neighborOrder of neighbors) {
          const result = runWithOrder(
            sector,
            neighborOrder,
            tableIndex,
            id,
            tableW,
            tableH,
            cfg
          );
          nextCandidates.push(result);
        }
      }

      nextCandidates.sort((a, b) => a.score - b.score);
      beam = nextCandidates.slice(0, cfg.beamWidth);
    }
  }

  // 4) Legjobb sorrend kiválasztása
  const bestOrder = beam[0].order;

  // 5) Végleges futtatás a legjobb sorrenddel (most már globális counterrel)
  const remainingFinal = bestOrder.map((r) => ({ ...r }));
  const placedFinal = [];
  const cutsFinal = [];

  processSectorFillOnTheFly(
    sector,
    remainingFinal,
    placedFinal,
    tableIndex,
    id,
    cutsFinal,
    "optimize",
    TRIM
  );

  return {
    placedRects: placedFinal,
    cuts: cutsFinal,
    remainingRects: remainingFinal,
    // Debug info
    _beamStats: {
      bestScore: beam[0].score,
      bestWasteRatio: beam[0].wasteRatio,
      bestPlacedRatio: beam[0].placedRatio,
      candidatesEvaluated: cfg.seedCount
    }
  };
}

// ─────────────────────────────────────────────
// Több tábla optimalizálása egymás után
// (minden tábla a maradék rectekkel dolgozik)
// ─────────────────────────────────────────────
export function optimizeMultipleTables(
  tables, // [{ sector, tableW, tableH, tableIndex, id }]
  inputRects,
  config = DEFAULT_BEAM_CONFIG
) {
  const allPlaced = [];
  const allCuts = [];
  let remaining = inputRects.map((r) => ({ ...r }));

  for (const table of tables) {
    if (remaining.length === 0) break;

    const { sector, tableW, tableH, tableIndex, id } = table;

    const result = optimizeSectorFill(
      sector,
      remaining,
      tableIndex,
      id,
      tableW,
      tableH,
      config
    );

    allPlaced.push(...result.placedRects);
    allCuts.push(...result.cuts);
    remaining = result.remainingRects;
  }

  return {
    placedRects: allPlaced,
    cuts: allCuts,
    remainingRects: remaining
  };
}
